import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getPageContent, getAllPages } from '../firebase/firebase-config';

function DynamicPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true);
      try {
        // Get all pages to find the one matching the slug
        const allPages = await getAllPages();
        let foundPage = null;
        
        // Search for page by slug
        for (const [pageId, pageData] of Object.entries(allPages)) {
          if (pageData.slug === slug || pageId === slug) {
            // Get full page content
            const pageContent = await getPageContent(pageId);
            foundPage = { id: pageId, ...pageContent };
            break;
          }
        }
        
        setPage(foundPage);
      } catch (error) {
        console.error('Error loading page:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, [slug]);

  if (loading) {
    return (
      <div className="dynamic-loading">
        <div className="loading-spinner"></div>
        <p>Loading page...</p>
      </div>
    );
  }

  if (!page) {
    return (
      <div className="error-container">
        <div className="error-content">
          <h1>404 - Page Not Found</h1>
          <p>The page you're looking for doesn't exist or has been moved.</p>
          <a href="/" className="home-link">Return to Homepage</a>
        </div>
      </div>
    );
  }

  const primaryColor = page.primaryColor || settings.primaryColor || '#1a3a6b';

  return (
    <div className="dynamic-page">
      {/* Hero Section if exists */}
      {page.heroTitle && (
        <section className="page-hero" style={{
          backgroundImage: page.heroImage ? `url(${page.heroImage})` : `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}>
          <div className="hero-overlay">
            <div className="container">
              <h1>{page.heroTitle || page.title}</h1>
              {page.heroSubtitle && <p>{page.heroSubtitle}</p>}
            </div>
          </div>
        </section>
      )}

      {/* Page Content */}
      <section className="page-content">
        <div className="container">
          {/* Page Title */}
          {!page.heroTitle && <h1 className="page-title">{page.title || slug}</h1>}
          
          {/* Main Content */}
          {page.content && (
            <div 
              className="content-body" 
              dangerouslySetInnerHTML={{ __html: page.content }} 
            />
          )}

          {/* Features Section */}
          {page.features && page.features.length > 0 && (
            <div className="features-section">
              <h2>{page.featuresTitle || 'Key Features'}</h2>
              <div className="features-grid">
                {page.features.map((feature, index) => (
                  <div key={index} className="feature-item">
                    <div className="feature-icon">{feature.icon || '📚'}</div>
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Gallery Section */}
          {page.gallery && page.gallery.length > 0 && (
            <div className="gallery-section">
              <h2>{page.galleryTitle || 'Gallery'}</h2>
              <div className="gallery-grid">
                {page.gallery.map((image, index) => (
                  <div key={index} className="gallery-image">
                    <img src={image.url} alt={image.caption || `Image ${index + 1}`} />
                    {image.caption && <p className="image-caption">{image.caption}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Files/Documents Section */}
          {page.documents && page.documents.length > 0 && (
            <div className="documents-section">
              <h2>{page.documentsTitle || 'Downloads'}</h2>
              <div className="documents-list">
                {page.documents.map((doc, index) => (
                  <div key={index} className="document-item">
                    <span className="doc-icon">📄</span>
                    <div className="doc-info">
                      <h4>{doc.title || doc.name}</h4>
                      {doc.description && <p>{doc.description}</p>}
                    </div>
                    <a href={doc.url} target="_blank" className="download-btn">Download</a>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Table Section */}
          {page.tableData && page.tableData.rows && page.tableData.rows.length > 0 && (
            <div className="table-section">
              <h2>{page.tableData.title || 'Information Table'}</h2>
              <table className="data-table">
                <thead>
                  <tr>
                    {page.tableData.headers.map((header, index) => (
                      <th key={index}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {page.tableData.rows.map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {row.map((cell, cellIndex) => (
                        <td key={cellIndex}>{cell}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <style>{`
        .dynamic-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 60vh;
        }
        
        .loading-spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #e2e8f0;
          border-top-color: ${primaryColor};
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        .error-container {
          min-height: 60vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f8fafc;
        }
        
        .error-content {
          text-align: center;
          padding: 40px;
        }
        
        .error-content h1 {
          font-size: 48px;
          color: #1e293b;
          margin-bottom: 16px;
        }
        
        .error-content p {
          color: #64748b;
          margin-bottom: 24px;
        }
        
        .home-link {
          display: inline-block;
          padding: 12px 24px;
          background: ${primaryColor};
          color: white;
          text-decoration: none;
          border-radius: 8px;
        }
        
        .page-hero {
          min-height: 300px;
          position: relative;
        }
        
        .hero-overlay {
          background: rgba(0,0,0,0.5);
          min-height: 300px;
          display: flex;
          align-items: center;
          text-align: center;
          color: white;
        }
        
        .hero-overlay h1 {
          font-size: 48px;
          margin-bottom: 16px;
        }
        
        .hero-overlay p {
          font-size: 18px;
        }
        
        .page-content {
          padding: 60px 0;
          background: #ffffff;
        }
        
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }
        
        .page-title {
          font-size: 36px;
          color: ${primaryColor};
          margin-bottom: 30px;
          text-align: center;
        }
        
        .content-body {
          font-size: 16px;
          line-height: 1.8;
          color: #334155;
        }
        
        .content-body h2 {
          color: ${primaryColor};
          margin: 30px 0 15px;
        }
        
        .content-body p {
          margin-bottom: 20px;
        }
        
        .features-section,
        .gallery-section,
        .documents-section,
        .table-section {
          margin-top: 50px;
        }
        
        .features-section h2,
        .gallery-section h2,
        .documents-section h2,
        .table-section h2 {
          font-size: 28px;
          color: ${primaryColor};
          text-align: center;
          margin-bottom: 30px;
        }
        
        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 30px;
        }
        
        .feature-item {
          text-align: center;
          padding: 30px;
          background: #f8fafc;
          border-radius: 16px;
          transition: transform 0.2s;
        }
        
        .feature-item:hover {
          transform: translateY(-5px);
        }
        
        .feature-icon {
          font-size: 48px;
          margin-bottom: 16px;
        }
        
        .feature-item h3 {
          margin-bottom: 12px;
          color: ${primaryColor};
        }
        
        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
        }
        
        .gallery-image {
          background: #f8fafc;
          border-radius: 12px;
          overflow: hidden;
        }
        
        .gallery-image img {
          width: 100%;
          height: 200px;
          object-fit: cover;
        }
        
        .image-caption {
          padding: 12px;
          text-align: center;
          font-size: 14px;
          color: #64748b;
        }
        
        .documents-list {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }
        
        .document-item {
          display: flex;
          align-items: center;
          gap: 20px;
          padding: 20px;
          background: #f8fafc;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
        }
        
        .doc-icon {
          font-size: 32px;
        }
        
        .doc-info {
          flex: 1;
        }
        
        .doc-info h4 {
          margin-bottom: 5px;
          color: ${primaryColor};
        }
        
        .doc-info p {
          font-size: 13px;
          color: #64748b;
        }
        
        .download-btn {
          padding: 8px 20px;
          background: ${primaryColor};
          color: white;
          text-decoration: none;
          border-radius: 8px;
          transition: background 0.2s;
        }
        
        .download-btn:hover {
          background: ${primaryColor}dd;
        }
        
        .data-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .data-table th {
          background: ${primaryColor};
          color: white;
          padding: 12px;
          text-align: left;
          font-weight: 600;
        }
        
        .data-table td {
          padding: 12px;
          border-bottom: 1px solid #e2e8f0;
        }
        
        .data-table tr:hover td {
          background: #f8fafc;
        }
        
        @media (max-width: 768px) {
          .hero-overlay h1 {
            font-size: 32px;
          }
          
          .page-title {
            font-size: 28px;
          }
          
          .features-grid {
            grid-template-columns: 1fr;
          }
          
          .gallery-grid {
            grid-template-columns: 1fr;
          }
          
          .document-item {
            flex-direction: column;
            text-align: center;
          }
          
          .data-table {
            font-size: 12px;
          }
        }
      `}</style>
    </div>
  );
}

export default DynamicPage;